import React from 'react';
import { ChartSchema } from '../../../types/types';

interface HorizontalBarChartRendererProps {
  series: ChartSchema['series'];
  xScale: (value: string | number) => number;
  yScale: (value: number) => number;
  yMinMax?: { min: number; max: number };
}

/**
 * Horizontal Bar Chart Renderer Component
 */
export const HorizontalBarChartRenderer: React.FC<HorizontalBarChartRendererProps> = ({
  series,
  xScale,
  yScale,
  yMinMax,
}) => {
  // Get unique x values for bar positioning
  const uniqueXValues = Array.from(new Set(series.flatMap(s => s.data.map(d => d.x))));
  const seriesCount = series.length;

  // Calculate the height for each bar group
  const maxBarHeight = 40; // Maximum height of a single bar
  const barHeight = Math.min(maxBarHeight, 80 / seriesCount); // Ensure bars aren't too tall

  // Get the baseline for the x-axis (always 0 for bar charts)
  // Make sure the baseline is visible within the chart area
  const minValue = yMinMax?.min || 0;
  const maxValue = yMinMax?.max || 100;

  // Use 0 as baseline, but ensure it's within the visible range
  const baselineValue = Math.max(minValue, Math.min(maxValue, 0));
  const baselineX = xScale(baselineValue);

  return (
    <g className="horizontal-bar-chart">
      {series.map((series, seriesIndex) => (
        <g key={`bar-series-${series.id}`} className="bar-series">
          {series.data.map((point, i) => {
            // For horizontal bar charts, categories go on the y-axis and values on the x-axis
            // Get the y-position based on the category (point.x)
            const categoryIndex = uniqueXValues.indexOf(point.x);
            const yCenter = yScale(categoryIndex);

            // Get the x-position based on the value (point.y)
            const xValue = xScale(point.y);

            // Calculate y position with offset for grouped bars
            const yOffset = (seriesIndex - (seriesCount - 1) / 2) * barHeight;
            const y = yCenter + yOffset;

            // For horizontal bar charts, we always draw from the baseline (0) left or right
            // Calculate the correct width and position based on value
            let barX, barWidth;

            if (point.y >= baselineValue) {
              // Positive values - draw right from baseline
              barX = baselineX;
              barWidth = xValue - baselineX;
            } else {
              // Negative values - draw left from baseline
              barX = xValue;
              barWidth = baselineX - xValue;
            }

            // Ensure minimum width for visibility
            barWidth = Math.max(barWidth, 1);

            // If the value is outside the visible range, don't render the bar
            if (point.y < minValue || point.y > maxValue) {
              barWidth = 0;
            }

            return (
              <g key={`bar-${series.id}-${i}`} className="horizontal-bar">
                <rect
                  x={barX}
                  y={y - barHeight / 2 + barHeight * 0.05}
                  width={barWidth}
                  height={barHeight * 0.9} // 90% of calculated height to add spacing
                  fill={series.color}
                  opacity={0.9}
                  rx={2} // Rounded corners
                />

                {/* Value label */}
                {point.y !== 0 && (
                  <text
                    x={point.y >= baselineValue ? barX + barWidth + 5 : barX - 5}
                    y={y}
                    textAnchor={point.y >= baselineValue ? 'start' : 'end'}
                    dominantBaseline="middle"
                    fontSize="10"
                    fill="#666"
                  >
                    {point.y}
                  </text>
                )}
              </g>
            );
          })}
        </g>
      ))}
    </g>
  );
};

export default HorizontalBarChartRenderer;
